/**
 * One figure on the executive screen (REQ-021 AC-001, AC-003, AC-005, AC-008).
 *
 * The whole requirement turns on what this component does when there is no
 * number. A gauge in this repository read zero for months because the code
 * writing it had been deleted, and zero is a plausible number — so an absent
 * figure here is drawn as words, in a tile that does not look like a small
 * value: no digits, no dash standing alone, and a sentence naming which of the
 * two absences this is. A reader who has to ask "is that a real zero?" is
 * looking at a dashboard that has already failed.
 *
 * The definition travels with the figure rather than living on another screen.
 * A number nobody can explain is a number nobody should act on (AC-010), so
 * the formula, the source and the owner are one click away from every tile —
 * and so are the records composing it, beside the definition and in the same
 * form. The two answer one question between them: what this number means, and
 * what it is actually made of. The trace mounts only when it is opened, so the
 * screen still costs one request for the figures and one for each trend.
 */
import {
  AlertTriangle,
  ChevronDown,
  HelpCircle,
  Layers,
  Minus,
} from "lucide-react";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import { useState } from "react";
import MetricTrace from "./MetricTrace";
import MetricTrend from "./MetricTrend";
import {
  formatValue,
  milestoneLabel,
  refreshLabel,
  unavailableReason,
  type MetricReading,
} from "./metrics";

const badge =
  "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium";

function Definition({ metric }: { metric: MetricReading }) {
  return (
    <dl className="mt-3 space-y-2 border-t border-gray-100 pt-3 text-xs text-gray-600">
      <div>
        <dt className="font-medium text-gray-500">Formula</dt>
        <dd>{metric.formula}</dd>
      </div>
      <div>
        <dt className="font-medium text-gray-500">Source</dt>
        <dd>{metric.source}</dd>
      </div>
      <div>
        <dt className="font-medium text-gray-500">Owner</dt>
        <dd>{metric.owner}</dd>
      </div>
      <div>
        <dt className="font-medium text-gray-500">Defined on</dt>
        <dd>
          {metric.definedOn} · refreshed {refreshLabel(metric.refreshSeconds)}
        </dd>
      </div>
    </dl>
  );
}

export default function MetricFigure({ metric }: { metric: MetricReading }) {
  const fmt = useLocaleFormat();
  const [showDefinition, setShowDefinition] = useState(false);
  const [showTrace, setShowTrace] = useState(false);
  const measured = metric.value.status === "ok";

  return (
    <section
      data-testid={`figure-${metric.id}`}
      className={`rounded-lg border bg-white p-4 ${
        measured ? "border-gray-200" : "border-amber-200 bg-amber-50/40"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-medium text-gray-700">{metric.label}</h3>
        <div className="flex flex-wrap justify-end gap-1">
          {/* AC-008: staleness is written on the figure. A reader who has to
              hover to learn that a number stopped moving will not hover. */}
          {metric.freshness.stale && (
            <span className={`${badge} bg-red-100 text-red-800`}>
              <AlertTriangle className="h-3 w-3" /> Stale
            </span>
          )}
          {metric.freshness.sampleRegression && (
            <span className={`${badge} bg-red-100 text-red-800`}>
              No records left
            </span>
          )}
          {measured && metric.provisional && (
            <span className={`${badge} bg-amber-100 text-amber-800`}>
              Provisional
            </span>
          )}
        </div>
      </div>

      {metric.value.status === "ok" ? (
        <p className="mt-2 text-2xl font-semibold tabular-nums text-gray-900">
          {formatValue(metric.value.value, metric.unit)}
        </p>
      ) : metric.value.status === "insufficient_sample" ? (
        <div className="mt-2">
          <p className="text-base font-semibold text-amber-800">
            Not enough data
          </p>
          <p className="mt-1 text-xs text-amber-900">
            Measured over {metric.sampleSize} records, which is too few to
            report as a figure.
          </p>
        </div>
      ) : (
        <div className="mt-2">
          <p className="flex items-center gap-1.5 text-base font-semibold text-amber-800">
            <Minus className="h-4 w-4" aria-hidden="true" /> Not measured
          </p>
          <p className="mt-1 text-xs text-amber-900">
            {unavailableReason(metric)}
          </p>
        </div>
      )}

      {measured && metric.provisional && (
        <p className="mt-1 text-xs text-amber-800">
          Provisional: computed on {metric.sampleSize} subjects, below the{" "}
          {metric.provisionalBelow} this metric needs (AC-005).
        </p>
      )}

      {metric.freshness.stale && metric.dataAsOf && (
        <p className="mt-1 text-xs text-red-700">
          Stale: the data behind this last moved {fmt.dateTime(metric.dataAsOf)}
          , longer ago than twice its refresh interval.
        </p>
      )}

      {metric.freshness.ageUnknown && (
        <p className="mt-1 text-xs text-amber-800">
          This source cannot say when its data last moved, so nothing here
          proves the figure is current.
        </p>
      )}

      {metric.target && (
        <p className="mt-2 text-xs text-gray-600">
          Target {metric.target.targetValue} by{" "}
          {milestoneLabel(metric.target.milestone)} ({metric.target.source},
          plan month {metric.target.planMonth})
          {metric.target.attainment !== undefined
            ? ` · ${Math.round(metric.target.attainment * 100)}% of it`
            : " · nothing to compare"}
        </p>
      )}

      <MetricTrend metric={metric} />

      <div className="mt-2 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => setShowDefinition((open) => !open)}
          aria-expanded={showDefinition}
          className="inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:text-sky-900"
        >
          <HelpCircle className="h-3.5 w-3.5" />
          Definition
          <ChevronDown
            className={`h-3 w-3 transition-transform ${showDefinition ? "rotate-180" : ""}`}
          />
        </button>

        <button
          type="button"
          onClick={() => setShowTrace((open) => !open)}
          aria-expanded={showTrace}
          className="inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:text-sky-900"
        >
          <Layers className="h-3.5 w-3.5" />
          What composes this
          <ChevronDown
            className={`h-3 w-3 transition-transform ${showTrace ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {showDefinition && <Definition metric={metric} />}
      {showTrace && <MetricTrace metricId={metric.id} />}
    </section>
  );
}
